/**
 * Meta Pixel Loader Service
 *
 * Dynamically loads and initializes the Meta Pixel script.
 * Only loads when marketing consent has been granted.
 * Idempotent: multiple calls to enableMetaPixel() are safe.
 */

import { getMarketingConsent } from '../consent/consentManager';

const PIXEL_ID = '1570249814227358';
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

let pixelLoaded = false;
let loading = false;
let loadFailed = false;

/**
 * Initialize the fbq global function (Meta Pixel queue stub).
 * This sets up the queue so events can be buffered before the script loads.
 */
function initFbqStub(): void {
  if (window.fbq) return;

  const queue: unknown[][] = [];

  const fbq = Object.assign(
    function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        queue.push(args);
      }
    },
    {
      callMethod: undefined as ((...args: unknown[]) => void) | undefined,
      queue,
      loaded: true,
      version: '2.0',
      push: undefined as unknown,
    },
  );

  // Self-reference for push
  fbq.push = fbq;

  window.fbq = fbq as unknown as NonNullable<Window['fbq']>;
  if (!window._fbq) window._fbq = fbq;
}

/**
 * Inject the Meta Pixel script tag into the document.
 * Returns a promise that resolves when the script loads or rejects on error.
 */
function injectScript(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Meta Pixel script'));

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });
}

/**
 * Load the Meta Pixel script and fire a PageView event.
 * Idempotent — calling multiple times will not duplicate the script or PageView.
 */
export async function enableMetaPixel(): Promise<void> {
  if (pixelLoaded || loading || loadFailed) return;
  loading = true;

  try {
    initFbqStub();
    window.fbq!('init', PIXEL_ID);
    window.fbq!('track', 'PageView');
    await injectScript();
    pixelLoaded = true;
  } catch (err) {
    console.error('[MetaPixel] Failed to load:', err);
    loadFailed = true;
  }
  loading = false;
}

/**
 * Track a custom event via Meta Pixel.
 * Silently no-ops if the pixel isn't loaded or consent isn't granted.
 */
export function trackEvent(eventName: string, params?: Record<string, any>): void {
  if (!pixelLoaded || !getMarketingConsent()) return;
  if (!window.fbq) return;

  if (params) {
    window.fbq('track', eventName, params);
  } else {
    window.fbq('track', eventName);
  }
}

/**
 * Check if the Meta Pixel script has been loaded.
 */
export function isPixelLoaded(): boolean {
  return pixelLoaded;
}

/**
 * Reset internal state (for testing only).
 */
export function _resetPixelState(): void {
  pixelLoaded = false;
  loading = false;
  loadFailed = false;
}
